import React, { useState, useRef, useEffect, useCallback } from "react";
import { Mic, ArrowRight, Loader2, Check, X, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { useNavigate, useLocation } from "react-router-dom";
import { analyzeCapture, confirmCapture, getEntries, updateEntry } from "../lib/api";
import TalkItThrough from "../components/TalkItThrough";
import { deleteWithUndo } from "../lib/items";
import { TypeBadge, DomainBadge, UrgencyBadge, OwnerBadge } from "../components/Badges";
import DraftReview from "../components/DraftReview";
import VoiceRecorder from "../components/VoiceRecorder";
import ItemActionsMenu from "../components/ItemActionsMenu";
import ItemDetailSheet from "../components/ItemDetailSheet";
import { WorthALookCard } from "../components/WorthALookCard";
import { AnchorGroupCard } from "../components/AnchorGroupCard";
import ReviewNudgeChip from "../components/ReviewNudgeChip";
import ResurfaceBanner from "../components/ResurfaceBanner";
import CueStatus from "../components/CueStatus";
import EditableCue from "../components/EditableCue";
import PushPrompt, { hasAskedPush } from "../components/PushPrompt";
import { pushSupported, notificationPermission, isIosNeedsInstall } from "../lib/push";
import { useAuth } from "../contexts/AuthContext";
import { getResurfacing, isOverdue, activeLoad, captureHeadline, headlineRepresentsReview, worthALook, returnSnoozeUntil, worthALookGroups, anchorGroupSnoozeUntil } from "../lib/resurface";
import { SHARE_CACHE, SHARE_TTL_MS, combinedShareText } from "../lib/share";
import { ENABLE_ANDROID_SHARE_TARGET } from "../config/featureFlags";
import { fmtDateTime, fmtTime } from "../lib/meta";

// First-run example chips. Tapping one fills the capture box. Hidden once the user has any item.
const ONBOARD_CHIPS = [
  "Remind me to call Mum this weekend",
  "Sort school uniform before September",
  "Book the dentist for the kids",
  "Chase the car insurance renewal",
];

// The Capture headline is derived LIVE from the shared activeLoad() model (see lib/resurface),
// so it always agrees with Inbox/Ahead and updates the instant items change — capture, done,
// delete, restore, snooze, re-date, or Start fresh. No cached/baked-in counts.

function ThinkingCard() {
  return (
    <div
      className="rise-in rounded-2xl bg-[var(--surface)] border border-[var(--line)] shadow-[0_2px_10px_rgba(26,28,26,0.04)] p-6 md:p-7"
      data-testid="capture-thinking"
    >
      <div className="flex items-center gap-3">
        <span className="h-9 w-9 rounded-full bg-[var(--surface2)] flex items-center justify-center">
          <span className="h-2.5 w-2.5 rounded-full bg-[var(--pine)] breathe" />
        </span>
        <div>
          <p className="font-heading font-medium text-[var(--ink)]">Holding this…</p>
          <p className="text-sm text-[var(--ink3)]">Sorting it so nothing gets lost.</p>
        </div>
      </div>
      <div className="mt-5 space-y-2.5">
        <div className="skeleton h-3.5 w-3/5" />
        <div className="skeleton h-3 w-2/5" />
        <div className="skeleton h-7 w-44 rounded-full mt-1" />
      </div>
    </div>
  );
}

function ResultCard({ entry, onCueApply }) {
  const [showWhy, setShowWhy] = useState(false);
  return (
    <div
      className="settle-in heldall-card bg-[var(--surface2)] border border-[var(--line)] p-6 md:p-7"
      data-testid="capture-result"
    >
      <p className="text-[var(--ink3)] text-[15px] leading-relaxed mb-5" data-testid="capture-reassurance">
        Got it. I've held onto the original, sorted what needs doing, and set when to bring it back.
      </p>

      <p className="text-xs tracking-wide font-medium text-[var(--ink5)] mb-1">you said</p>
      <p className="text-[var(--ink)] text-lg leading-relaxed mb-5">“{entry.raw_text}”</p>

      <p className="text-xs tracking-wide font-medium text-[var(--ink5)] mb-1.5">i understood</p>
      <p className="font-serif-display text-[var(--ink)] text-2xl leading-snug mb-4">{entry.interpreted_summary}</p>

      <div className="flex flex-wrap gap-2 mb-4">
        <UrgencyBadge urgency={isOverdue(entry) ? "OVERDUE" : entry.urgency} />
        {entry.owner !== "me" && <OwnerBadge owner={entry.owner} name={entry.assignee_name} />}
        <TypeBadge type={entry.type} subtle />
        <DomainBadge domain={entry.domain} subtle />
        {entry.needs_review && (
          <span className="inline-flex items-center rounded-full border border-[var(--pine-line)] bg-[var(--pine-soft)] px-2.5 py-0.5 text-xs font-semibold text-[var(--pine)]">
            Worth a glance
          </span>
        )}
      </div>

      {entry.owner !== "me" && (
        <p className="text-sm text-[var(--slate)] bg-[var(--slate-soft)] border border-[var(--slate-soft)] rounded-xl px-3.5 py-2.5 mb-4" data-testid="handoff-hint">
          It moves to their list right away. You'll see when they've marked it done or handed it back.
        </p>
      )}

      <div className="mb-3">
        <p className="text-xs tracking-wide font-medium text-[var(--ink5)] mb-2">when it comes back</p>
        <EditableCue entry={entry} onApply={onCueApply} testid="capture-cue-status" />
      </div>

      {entry.explanation && (
        <div className="mt-1">
          <button
            type="button"
            data-testid="capture-why-toggle"
            onClick={() => setShowWhy((s) => !s)}
            aria-expanded={showWhy}
            className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--ink5)] hover:text-[var(--pine)] transition-colors duration-200"
          >
            {showWhy ? "Hide why" : "Why?"}
          </button>
          {showWhy && (
            <p className="mt-2 text-sm text-[var(--ink3)] italic border-l-2 border-[var(--pine-line)] pl-3 rise-in" data-testid="capture-explanation">
              {entry.explanation}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// --- Android share-to-Heldall (v1) ---------------------------------------------------------
// Payloads handed over by the service worker live in a transient cache. Capture consumes them
// (delete-on-read, 5-min TTL), routes them through the EXISTING review flow, and never auto-saves.
async function consumeSharedPayload() {
  if (!("caches" in window)) return null;
  const cache = await caches.open(SHARE_CACHE);
  const metaRes = await cache.match("/__share_meta");
  if (!metaRes) return null;
  const meta = await metaRes.json();
  let file = null;
  const fileRes = await cache.match("/__share_file");
  if (fileRes) {
    const blob = await fileRes.blob();
    const f = (meta.files && meta.files[0]) || {};
    file = new File([blob], f.name || "shared-image", { type: f.type || blob.type || "image/png" });
  }
  await cache.delete("/__share_meta");
  await cache.delete("/__share_file");
  if (meta.error || !meta.receivedAt || Date.now() - meta.receivedAt > SHARE_TTL_MS) {
    return { stale: !meta.error, error: !!meta.error };
  }
  return { meta, file };
}

export default function Capture() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [last, setLast] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [showPushPrompt, setShowPushPrompt] = useState(false);
  const [landed, setLanded] = useState(false);  // brief "settled into held" cue after a capture
  const [voiceOpen, setVoiceOpen] = useState(false);  // MediaRecorder voice capture overlay

  // Ask only after a meaningful action (a successful capture), once, never on
  // cold load, and never when iOS still needs Add-to-Home-Screen first.
  const maybePromptPush = useCallback(() => {
    if (hasAskedPush()) return;
    if (!pushSupported()) return;
    if (isIosNeedsInstall()) return;
    if (notificationPermission() !== "default") return;
    setTimeout(() => setShowPushPrompt(true), 900);
  }, []);
  const [savingDrafts, setSavingDrafts] = useState(false);
  const [recent, setRecent] = useState([]);
  const [talkOpen, setTalkOpen] = useState(false);
  const [talkText, setTalkText] = useState("");
  const [talkSource, setTalkSource] = useState("typed");
  const [shareState, setShareState] = useState(null); // null | "reading" | "empty" | "error"
  const shareHandledRef = useRef(false);
  const [imageFile, setImageFile] = useState(null);
  const [audioFile, setAudioFile] = useState(null);
  const imageInputRef = useRef(null);
  const [allEntries, setAllEntries] = useState([]);
  const [detailId, setDetailId] = useState(null);
  const [worthSeen, setWorthSeen] = useState(
    () => localStorage.getItem("heldall_wal_seen") === new Date().toISOString().slice(0, 10)
  );
  const [showProof, setShowProof] = useState(false);
  const [inviteDismissed, setInviteDismissed] = useState(false);
  const recentLoadedRef = useRef(false);
  const initialEmptyRef = useRef(false);
  const taRef = useRef(null);

  // Voice capture now records audio (MediaRecorder) and transcribes server-side (whisper-1),
  // so there is no in-browser Web Speech dictation (no OS earcon, no partial-text flooding).
  // The finished recording opens the SAME review surface via TalkItThrough (initialAudioFile).
  const onVoiceComplete = useCallback((file) => {
    setVoiceOpen(false);
    setText("");
    setLast(null);
    setImageFile(null);
    setAudioFile(file);
    setTalkOpen(true);
  }, []);

  // Android share-to-Heldall arrival. Runs once on mount: consume any payload the service worker
  // handed over and route it into the SAME review flow (TalkItThrough). Authenticated by virtue
  // of being inside the app shell; nothing is saved until the user confirms in review.
  useEffect(() => {
    if (shareHandledRef.current) return;
    shareHandledRef.current = true;
    const params = new URLSearchParams(location.search);
    const flagged = params.get("shared") === "1";
    const disabledLanding = params.get("share_disabled") === "1";
    if (flagged || disabledLanding) navigate("/", { replace: true }); // clear trigger
    (async () => {
      // Kill switch (or SW-disabled landing): do not process; purge any stale payload defensively.
      if (!ENABLE_ANDROID_SHARE_TARGET || disabledLanding) {
        try {
          if ("caches" in window) {
            const c = await caches.open(SHARE_CACHE);
            await c.delete("/__share_meta"); await c.delete("/__share_file");
          }
        } catch (_) { /* noop */ }
        if (disabledLanding) setShareState("disabled");
        return;
      }
      let payload = null;
      try { payload = await consumeSharedPayload(); } catch (_) { payload = { error: true }; }
      if (!payload) { if (flagged) setShareState("empty"); return; }
      if (payload.error) { setShareState("error"); return; }
      if (payload.stale) { setShareState(flagged ? "empty" : null); return; }
      const { meta, file } = payload;
      setShareState("reading");
      setText(""); setLast(null);
      if (file) {
        // v1: prefer the image. Accompanying text/URL is NOT sent as context (image endpoint
        // does not accept it) — documented limitation.
        setTalkText(""); setAudioFile(null); setTalkSource("share");
        setImageFile(file); setTalkOpen(true); setShareState(null);
      } else {
        const combined = combinedShareText(meta);
        if (!combined) { setShareState("empty"); return; }
        setImageFile(null); setAudioFile(null); setTalkSource("share");
        setTalkText(combined); setTalkOpen(true); setShareState(null);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadRecent = useCallback(async () => {
    try {
      const entries = await getEntries();
      setAllEntries(entries);
      const top = entries.slice(0, 5);
      setRecent(top);
      // Capture a one-time baseline: was this user empty at first load this session?
      if (!recentLoadedRef.current) {
        recentLoadedRef.current = true;
        initialEmptyRef.current = entries.length === 0;
      }
    } catch (_) {}
  }, []);

  useEffect(() => { loadRecent(); }, [loadRecent]);

  // Init the per-user "invite nudge dismissed" flag from localStorage.
  useEffect(() => {
    const uid = user?.id;
    if (!uid) return;
    setInviteDismissed(localStorage.getItem(`heldall_invite_nudge_${uid}`) === "1");
  }, [user]);

  // First-capture proof moment: fires once, only for a user who was empty at first load this
  // session and then makes their first capture. Returning users (items present at load) never see
  // it, regardless of localStorage state. Dismissal persists via localStorage so it never repeats.
  useEffect(() => {
    const uid = user?.id;
    if (!uid) return;
    if (!recentLoadedRef.current) return;     // wait for the first load baseline
    if (!initialEmptyRef.current) return;     // user already had items at load: never show
    if (recent.length > 0 && !localStorage.getItem(`heldall_proof_${uid}`)) {
      setShowProof(true);
    }
  }, [recent, user]);

  const dismissProof = () => {
    const uid = user?.id;
    if (uid) localStorage.setItem(`heldall_proof_${uid}`, "1");
    setShowProof(false);
  };
  const dismissInviteNudge = () => {
    const uid = user?.id;
    if (uid) localStorage.setItem(`heldall_invite_nudge_${uid}`, "1");
    setInviteDismissed(true);
  };
  const fillFromChip = (t) => {
    setText(t);
    setTimeout(() => taRef.current?.focus(), 0);
  };

  // Context-aware session greeting removed: the headline below is now derived live from the
  // shared activeLoad() model, so it can never show stale overdue/due counts after Start fresh.

  useEffect(() => {
    taRef.current?.focus();
  }, []);

  useEffect(() => {
    const ta = taRef.current;
    if (ta) { ta.style.height = "auto"; ta.style.height = Math.min(ta.scrollHeight, 360) + "px"; }
  }, [text]);

  // One intelligent surface: everything the user puts down — a single thought, a long
  // brain dump, or a pasted transcript — goes through the V2 engine. The review surface
  // (TalkItThrough) opens pre-filled and auto-processes. V1 is kept ONLY as an internal
  // fallback (captureV1Fallback) if the V2 endpoint ever fails.
  const handleDone = () => {
    if (submitting) return;
    const value = text.trim();
    if (!value) return;
    setTalkText(value);
    setTalkOpen(true);
    setText("");
    setLast(null);
  };

  // Secondary intake: upload a screenshot/photo. Validates client-side, then opens the SAME
  // review surface (TalkItThrough) which sends it to the vision->extraction path.
  const onImagePick = (e) => {
    const file = e.target.files && e.target.files[0];
    if (e.target) e.target.value = "";  // allow re-selecting the same file
    if (!file) return;
    const okType = ["image/jpeg", "image/jpg", "image/png", "image/webp"].includes((file.type || "").toLowerCase());
    if (!okType) { toast.error("Please choose a JPEG, PNG or WEBP image."); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("That image is over 10MB. Try a smaller screenshot."); return; }
    setText("");
    setLast(null);
    setImageFile(file);
    setTalkOpen(true);
  };

  const captureV1Fallback = async (value) => {
    if (submitting) return;
    const v = (value || "").trim();
    if (!v) return;
    setTalkOpen(false);
    setSubmitting(true);
    setLast(null);
    try {
      const res = await analyzeCapture(v);
      const flagged = res.is_compound || res.uncertain;
      const payload = res.drafts.map(({ _key, ...d }) => ({
        ...d, needs_review: d.needs_review || flagged,
      }));
      const saved = await confirmCapture(payload);
      setLanded(true);
      setTimeout(() => setLanded(false), 460);
      setText("");
      loadRecent();
      if (saved.length === 1 && !res.is_compound && !res.uncertain) {
        setLast(saved[0]);
        setDrafts([]);
        toast.success("Held. It won't get lost.");
        if (!saved[0].needs_review) maybePromptPush();
      } else {
        setLast(null);
        setDrafts(saved.map((s) => ({ ...s, _key: s.id })));
        toast.success("Saved. You can review these later.");
      }
    } catch (e) {
      toast.error("Couldn't capture that. Try again.");
      setText(v);
    } finally {
      setSubmitting(false);
    }
  };

  const tzOffsetMin = -new Date().getTimezoneOffset();

  // Drafts are already-saved entries (keyed by real id); edits persist live.
  const updateDraft = (key, patch) => {
    setDrafts((ds) => ds.map((d) => (d._key === key ? { ...d, ...patch } : d)));
    updateEntry(key, { ...patch, tz_offset: tzOffsetMin })
      .then((updated) => {
        setDrafts((ds) => ds.map((d) => (d._key === key ? { ...updated, _key: key } : d)));
        loadRecent();
      })
      .catch(() => toast.error("Couldn't update that."));
  };

  const removeDraft = (key) => {
    deleteWithUndo(key, {
      onDeleted: () => { setDrafts((ds) => ds.filter((d) => d._key !== key)); loadRecent(); },
      onRestored: loadRecent,
    });
  };

  const editEntry = (id, changes) =>
    updateEntry(id, { ...changes, tz_offset: tzOffsetMin })
      .then(() => { loadRecent(); toast.success("Saved."); })
      .catch(() => toast.error("Couldn't save that."));

  const deleteEntryUndo = (id) => {
    setDetailId(null);
    deleteWithUndo(id, { onDeleted: loadRecent, onRestored: loadRecent });
  };

  const doneReview = async () => {
    const toClear = drafts.filter((d) => d.needs_review).map((d) => d._key);
    setDrafts([]);
    try { await Promise.all(toClear.map((id) => updateEntry(id, { needs_review: false }))); } catch (_) {}
    loadRecent();
    maybePromptPush();  // deferred to after the review moment, never on top of it
  };

  const onKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") { e.preventDefault(); handleDone(); }
  };

  const markResurfaceDone = async (id) => {
    setAllEntries((es) => es.map((e) => (e.id === id ? { ...e, status: "DONE" } : e)));
    try {
      await updateEntry(id, { status: "DONE" });
      toast.success("Done. One less thing to hold.");
      loadRecent();
    } catch (_) {
      toast.error("Couldn't update that.");
      loadRecent();
    }
  };

  const { overdue, dueToday } = getResurfacing(allEntries);
  const load = activeLoad(allEntries);
  const headline = captureHeadline(load, user?.name);

  // Context-aware return: imminent horizon items. Shown at most once per calendar day.
  const worthItems = worthALook(allEntries, new Date(), load);
  const todayKey = new Date().toISOString().slice(0, 10);
  const showWorth = !worthSeen && worthItems.length > 0;
  useEffect(() => {
    if (showWorth) localStorage.setItem("heldall_wal_seen", todayKey);
  }, [showWorth, todayKey]);
  const openWorth = (id) => { setWorthSeen(true); setDetailId(id); };
  const snoozeWorth = async (id) => {
    try { await updateEntry(id, { return_snoozed_until: returnSnoozeUntil() }); }
    finally { loadRecent(); }
  };

  // Anchor-date grouping: dated anchors within 14 days with open dependents.
  const anchorGroups = worthALookGroups(allEntries, new Date(), load);
  const showGroups = !worthSeen && anchorGroups.length > 0;
  useEffect(() => {
    if (showGroups) localStorage.setItem("heldall_wal_seen", todayKey);
  }, [showGroups, todayKey]);
  const openGroupItem = (id) => { setWorthSeen(true); setDetailId(id); };
  const notNowGroup = async (anchor) => {
    try { await updateEntry(anchor.id, { return_snoozed_until: anchorGroupSnoozeUntil(anchor) }); }
    finally { loadRecent(); }
  };

  const reviewItems = allEntries.filter((e) => e.needs_review && e.status === "ACTIVE");
  const reviewingNow = drafts.some((d) => d.needs_review);

  const openReviewQueue = useCallback(() => {
    const items = allEntries.filter((e) => e.needs_review && e.status === "ACTIVE");
    if (items.length === 0) return;
    setLast(null);
    setDrafts(items.map((s) => ({ ...s, _key: s.id })));
    setTimeout(() => {
      document.querySelector('[data-testid="draft-review"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  }, [allEntries]);

  // Inbox chip routes here with ?review=1 — open the saved review queue once.
  const reviewOpenedRef = useRef(false);
  useEffect(() => {
    const wants = new URLSearchParams(location.search).get("review") === "1";
    if (wants && !reviewOpenedRef.current && reviewItems.length > 0) {
      reviewOpenedRef.current = true;
      openReviewQueue();
      navigate("/", { replace: true });
    }
  }, [location.search, reviewItems.length, openReviewQueue, navigate]);

  const tzOffset = -new Date().getTimezoneOffset();
  const applyLastCue = async (iso) => {
    if (!last) return;
    const payload = iso === null
      ? { clear_cue: true, tz_offset: tzOffset }
      : { suggested_datetime: iso, tz_offset: tzOffset };
    try {
      const updated = await updateEntry(last.id, payload);
      setLast(updated);
      loadRecent();
      toast.success("Saved.");
    } catch (_) {
      toast.error("Couldn't update the cue.");
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-5 md:px-10 py-8 md:py-16">
      <div className="mb-7 md:mb-8">
        <h1 className="font-display text-[1.75rem] leading-[1.15] sm:text-4xl tracking-tight font-medium text-[var(--ink)]" data-testid="capture-greeting">
          {headline}
        </h1>
        <p className="text-[var(--ink3)] text-base mt-2.5 leading-relaxed">
          Say it, type it, or paste it. It won't get lost.
        </p>
        {!reviewingNow && !headlineRepresentsReview(headline) && (
          <div className="mt-4">
            <ReviewNudgeChip count={reviewItems.length} onClick={openReviewQueue} />
          </div>
        )}
      </div>

      {showWorth && (
        <WorthALookCard items={worthItems} onOpen={openWorth} onSnooze={snoozeWorth} />
      )}

      {showGroups && anchorGroups.map((card) => (
        <AnchorGroupCard key={card.anchor.id} card={card} onOpen={openGroupItem} onNotNow={notNowGroup} />
      ))}

      {(overdue.length > 0 || dueToday.length > 0) && (
        <div className="mb-8">
          <ResurfaceBanner
            overdue={overdue}
            dueToday={dueToday}
            onDone={markResurfaceDone}
            onOpen={setDetailId}
            compact
          />
        </div>
      )}

      <div className="rounded-2xl bg-[var(--surface2)] border border-[var(--line)] p-5 md:p-6">
        <textarea
          ref={taRef}
          data-testid="capture-input"
          className="capture-textarea w-full bg-transparent resize-none border-0 outline-none ring-0 text-xl md:text-2xl leading-relaxed text-[var(--ink)] min-h-[120px]"
          placeholder="What's on your mind? Say it, type it, or paste it…"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
        />

        <div className="flex items-center justify-between mt-4 pt-4 border-t border-[var(--line)]">
          <div className="flex items-center gap-3">
            <button
              data-testid="mic-capture-btn"
              onClick={() => setVoiceOpen(true)}
              title="Record your voice"
              className={`relative h-12 w-12 rounded-full flex items-center justify-center transition-all duration-200 bg-[var(--pine)] text-[var(--on-pine)] ${
                landed ? "held-ring--settle" : "held-ring hover:bg-[var(--pine-dark)]"
              }`}
            >
              <span className="held-halo" aria-hidden="true" />
              <Mic size={20} strokeWidth={1.75} />
            </button>
            <span className="text-sm text-[var(--ink3)]" data-testid="mic-status">
              Tap to record. Say everything — I'll sort it.
            </span>
          </div>

          <button
            data-testid="capture-submit-btn"
            onClick={handleDone}
            disabled={!text.trim() || submitting}
            className="inline-flex items-center gap-2 rounded-full bg-[var(--pine)] hover:bg-[var(--pine-dark)] disabled:opacity-40 disabled:cursor-not-allowed text-[var(--on-pine)] font-semibold px-6 py-3 text-sm shadow-[0_8px_24px_rgba(44,90,77,0.25)] transition-all duration-200"
          >
            {submitting ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} strokeWidth={2} />}
            I'm done
          </button>
        </div>

        <div className="mt-3 flex items-center">
          <input
            ref={imageInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/*"
            className="hidden"
            data-testid="capture-image-input"
            onChange={onImagePick}
          />
          <button
            type="button"
            data-testid="upload-image-btn"
            onClick={() => imageInputRef.current && imageInputRef.current.click()}
            className="inline-flex items-center gap-1.5 text-[13px] font-medium text-[var(--ink3)] hover:text-[var(--pine)] transition-colors duration-200"
          >
            <ImagePlus size={15} strokeWidth={1.9} />
            Upload image
          </button>
          <span className="ml-2 text-[12px] text-[var(--ink4)] hidden sm:inline">Screenshot a message, email or note.</span>
        </div>
      </div>

      <TalkItThrough
        open={talkOpen}
        initialText={talkText}
        autoProcess
        initialImageFile={imageFile}
        initialAudioFile={audioFile}
        initialSource={talkSource}
        onClose={() => { setTalkOpen(false); setTalkText(""); setImageFile(null); setAudioFile(null); setTalkSource("typed"); }}
        onSaved={loadRecent}
        onFallback={captureV1Fallback}
        onGo={(path) => { setTalkOpen(false); setTalkText(""); setImageFile(null); setAudioFile(null); setTalkSource("typed"); navigate(path); }}
      />

      <VoiceRecorder
        open={voiceOpen}
        onCancel={() => setVoiceOpen(false)}
        onComplete={onVoiceComplete}
      />

      {shareState && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-[var(--bg)]/95 backdrop-blur-sm px-6" data-testid="share-arrival">
          <div className="max-w-sm w-full text-center">
            {shareState === "reading" && (
              <>
                <Loader2 className="animate-spin text-[var(--pine)] mx-auto mb-4" size={26} />
                <p className="font-serif-display text-2xl text-[var(--ink)]">Reading what you shared…</p>
                <p className="text-[var(--ink3)] text-sm mt-2">Understanding what matters. Nothing's saved yet.</p>
              </>
            )}
            {(shareState === "empty" || shareState === "error") && (
              <>
                <p className="font-serif-display text-2xl text-[var(--ink)]">
                  {shareState === "error" ? "That didn't come through cleanly." : "Nothing arrived to read."}
                </p>
                <p className="text-[var(--ink3)] text-sm mt-2">
                  Nothing was saved. Try sharing it again, or open Heldall and paste it in.
                </p>
                <button
                  onClick={() => setShareState(null)}
                  className="mt-5 inline-flex items-center justify-center rounded-full bg-[var(--pine)] text-white px-5 py-2.5 text-sm font-semibold"
                  data-testid="share-arrival-dismiss"
                >
                  Continue to Heldall
                </button>
              </>
            )}
            {shareState === "disabled" && (
              <>
                <p className="font-serif-display text-2xl text-[var(--ink)]">Sharing to Heldall is not available right now.</p>
                <p className="text-[var(--ink3)] text-sm mt-2">Open Heldall and paste or upload it instead.</p>
                <button
                  onClick={() => setShareState(null)}
                  className="mt-5 inline-flex items-center justify-center rounded-full bg-[var(--pine)] text-white px-5 py-2.5 text-sm font-semibold"
                  data-testid="share-arrival-dismiss"
                >
                  Continue to Heldall
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {recent.length === 0 && !last && drafts.length === 0 && !submitting && (
        <div className="mt-6 px-1" data-testid="first-run-empty-state">
          <h2 className="font-serif-display text-2xl text-[var(--ink)] mb-1.5">Say it once. It won't get lost.</h2>
          <p className="text-[var(--ink3)] text-sm leading-relaxed mb-4">
            For the things you'd otherwise keep in your head. Reminders, life admin, appointments,
            things to hand off, and anything that needs to come back.
          </p>
          <div className="flex flex-wrap gap-2" data-testid="onboard-chips">
            {ONBOARD_CHIPS.map((c, i) => (
              <button
                key={i}
                type="button"
                data-testid={`onboard-chip-${i}`}
                onClick={() => fillFromChip(c)}
                className="rounded-full border border-[var(--line)] bg-[var(--surface)] px-3.5 py-2 text-sm text-[var(--ink2)] hover:border-[var(--pine)] hover:text-[var(--pine)] transition-all duration-200"
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      )}

      {recent.length > 0 && !user?.partner && !inviteDismissed && (
        <div className="mt-8 rounded-2xl bg-[var(--surface2)] border border-[var(--line)] px-5 py-4 flex items-center justify-between gap-4" data-testid="invite-nudge">
          <p className="text-sm text-[var(--ink2)] min-w-0">
            Using Heldall with someone else? Invite them when you're ready.
          </p>
          <div className="flex items-center gap-1.5 shrink-0">
            <button
              data-testid="invite-nudge-btn"
              onClick={() => navigate("/account")}
              className="inline-flex items-center gap-1.5 rounded-full bg-[var(--pine)] hover:bg-[var(--pine-dark)] text-[var(--on-pine)] font-semibold px-4 py-2 text-sm transition-all duration-200"
            >
              Invite someone
            </button>
            <button
              data-testid="invite-nudge-dismiss"
              onClick={dismissInviteNudge}
              className="p-2 rounded-lg text-[var(--ink4)] hover:bg-[var(--surface)]"
              aria-label="Dismiss"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {drafts.length > 0 && (
        <div className="mt-8">
          <DraftReview
            drafts={drafts}
            saving={savingDrafts}
            persisted
            onChange={updateDraft}
            onRemove={removeDraft}
            onSave={doneReview}
          />
        </div>
      )}

      {submitting && drafts.length === 0 && (
        <div className="mt-8">
          <ThinkingCard />
        </div>
      )}

      {last && !submitting && drafts.length === 0 && (
        <div className="mt-8">
          <ResultCard entry={last} onCueApply={applyLastCue} />
        </div>
      )}

      <div className="mt-14">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xs uppercase tracking-[0.2em] font-bold text-[var(--ink3)]">Recently captured</h2>
        </div>
        {recent.length === 0 ? (
          <p className="text-[var(--ink4)] text-sm">Nothing yet. Your first thought lands here.</p>
        ) : (
          <div className="space-y-3" data-testid="recent-captures">
            {recent.map((e) => (
              <div
                key={e.id}
                className="rounded-2xl bg-[var(--surface)] border border-[var(--line)] shadow-[0_2px_10px_rgba(26,28,26,0.04)] px-5 py-4 flex items-start justify-between gap-3 transition-all duration-200 hover:-translate-y-[1px] hover:shadow-[0_8px_28px_rgba(26,28,26,0.07)]"
                data-testid={`recent-item-${e.id}`}
              >
                <button
                  type="button"
                  data-testid={`open-detail-${e.id}`}
                  onClick={() => setDetailId(e.id)}
                  className="min-w-0 text-left group/title"
                >
                  <p className={`text-[var(--ink)] font-medium truncate group-hover/title:underline decoration-[var(--pine-line)] underline-offset-2 ${e.status === "DONE" ? "line-through text-[var(--ink4)]" : ""}`}>{e.interpreted_summary}</p>
                  <p className="text-xs text-[var(--ink5)] mt-0.5">{fmtTime(e.created_at)}</p>
                </button>
                <div className="flex items-center gap-1.5 shrink-0 justify-end">
                  <div className="flex flex-wrap gap-1.5 justify-end max-w-[40vw]">
                    {e.needs_review && (
                      <span className="inline-flex items-center rounded-full bg-[var(--pine-soft)] text-[var(--pine)] px-2.5 py-0.5 text-xs font-semibold" data-testid={`needs-review-${e.id}`}>
                        Needs review
                      </span>
                    )}
                    <UrgencyBadge urgency={isOverdue(e) ? "OVERDUE" : e.urgency} />
                    {e.owner !== "me" && <OwnerBadge owner={e.owner} name={e.assignee_name} />}
                    <TypeBadge type={e.type} subtle />
                  </div>
                  <ItemActionsMenu entry={e} onUpdated={loadRecent} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ItemDetailSheet
        entry={allEntries.find((e) => e.id === detailId) || null}
        relatedAnchor={(() => {
          const cur = allEntries.find((e) => e.id === detailId);
          return cur && cur.anchor_entry_id ? allEntries.find((e) => e.id === cur.anchor_entry_id) || null : null;
        })()}
        open={!!detailId}
        onOpenChange={(o) => { if (!o) setDetailId(null); }}
        onChange={editEntry}
        onDelete={deleteEntryUndo}
      />

      <PushPrompt
        open={showPushPrompt}
        onClose={() => setShowPushPrompt(false)}
        onEnabled={() => setShowPushPrompt(false)}
      />

      {showProof && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" data-testid="proof-moment">
          <div className="w-full max-w-sm rounded-2xl bg-[var(--surface)] border border-[var(--line)] shadow-2xl p-6 md:p-7 text-center settle-in">
            <span className="inline-flex h-12 w-12 rounded-full bg-[var(--pine-soft)] items-center justify-center text-[var(--pine)] mb-4">
              <Check size={24} strokeWidth={2.2} />
            </span>
            <h3 className="font-serif-display text-2xl text-[var(--ink)] mb-2">Held.</h3>
            <p className="text-sm text-[var(--ink3)] leading-relaxed mb-6">
              You said it once. Heldall understood what needed doing. It'll stay visible until it's handled.
            </p>
            <button
              data-testid="proof-moment-dismiss"
              onClick={dismissProof}
              className="w-full rounded-full bg-[var(--pine)] hover:bg-[var(--pine-dark)] text-[var(--on-pine)] font-semibold py-3 text-sm shadow-[0_8px_24px_rgba(44,90,77,0.25)] transition-all duration-200"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
